import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest, API_URL } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface GitHubInstallation {
  id: string;
  installationId: string;
  accountLogin: string;
  accountType: string;
  targetType: string;
  organizationId: string;
  isActive: boolean;
  suspendedAt: string | null;
  installedAt: string;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface GitHubRepository {
  id: string;
  owner: string;
  name: string;
  description: string | null;
  isPrivate: boolean;
  isEnabled: boolean;
}

interface GitHubState {
  currentInstallation: GitHubInstallation | null;
  installations: GitHubInstallation[];
  repositories: GitHubRepository[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: GitHubState = {
  currentInstallation: null,
  installations: [],
  repositories: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getInstallations = createAsyncThunk(
  'github/getInstallations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/github/installations?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getInstallation = createAsyncThunk(
  'github/getInstallation',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/github/installations/${installationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getRepositories = createAsyncThunk(
  'github/getRepositories',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/github/installations/${installationId}/repositories`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleRepository = createAsyncThunk(
  'github/toggleRepository',
  async ({ repositoryId, currentState }: { repositoryId: string; currentState: boolean }, { rejectWithValue }) => {
    try {
      await apiRequest(`/github/repositories/${repositoryId}/toggle`, { method: 'POST' });
      return { repositoryId, newState: !currentState };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const syncRepositories = createAsyncThunk(
  'github/syncRepositories',
  async (installationId: string, { rejectWithValue, dispatch }) => {
    try {
      await apiRequest(`/github/installations/${installationId}/sync-repos`, { method: 'POST' });
      // Fetch updated repositories
      const response = await dispatch(getRepositories(installationId)).unwrap();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectGitHub = createAsyncThunk(
  'github/disconnectGitHub',
  async (installationId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/github/installations/${installationId}`, { method: 'DELETE' });
      return installationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const initiateGitHubConnect = createAsyncThunk(
  'github/initiateConnect',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      // Step 1: Call API with authentication to get GitHub installation URL
      const response = await apiRequest(`/github/install?organizationId=${organizationId}`);
      const installUrl = response.response?.installUrl || response.installUrl;

      if (!installUrl) {
        throw new Error('No installation URL returned from API');
      }

      // Step 2: Redirect browser to GitHub
      window.location.href = installUrl;

      return { installUrl };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get installations
  [getInstallations.pending.type]: { status: 'loading' as const },
  [getInstallations.fulfilled.type]: (state: GitHubState, action: any) => {
    // Handle null response (404 - no installations found)
    if (!action.payload) {
      state.installations = [];
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    // Backend already filters by isActive: true
    const installations = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.installations || action.payload.installations || [];

    state.installations = installations;
    state.currentInstallation = installations[0] || null;
    state.status = 'success';
    state.error = '';
  },
  [getInstallations.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installations';
  },

  // Get single installation
  [getInstallation.pending.type]: { status: 'loading' as const },
  [getInstallation.fulfilled.type]: (state: GitHubState, action: any) => {
    // Handle null response (404 - installation not found)
    if (!action.payload) {
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: {...}, metadata: {...} }
    state.currentInstallation = action.payload.response || action.payload;
    state.status = 'success';
    state.error = '';
  },
  [getInstallation.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installation';
  },

  // Get repositories
  [getRepositories.pending.type]: { status: 'loading' as const },
  [getRepositories.fulfilled.type]: (state: GitHubState, action: any) => {
    // Handle null response (404 - repositories not found)
    if (!action.payload) {
      state.repositories = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    state.repositories = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.repositories || action.payload.repositories || [];
    state.status = 'success';
    state.error = '';
  },
  [getRepositories.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch repositories';
  },

  // Toggle repository
  [toggleRepository.pending.type]: (state: GitHubState) => {
    state.status = 'loading';
  },
  [toggleRepository.fulfilled.type]: (state: GitHubState, action: any) => {
    const { repositoryId, newState } = action.payload;
    const repo = state.repositories.find((r) => r.id === repositoryId);
    if (repo) {
      repo.isEnabled = newState;
    }
    state.status = 'success';
    state.error = '';
  },
  [toggleRepository.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to toggle repository';
  },

  // Sync repositories
  [syncRepositories.pending.type]: { status: 'loading' as const },
  [syncRepositories.fulfilled.type]: (state: GitHubState) => {
    state.status = 'success';
    state.error = '';
  },
  [syncRepositories.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to sync repositories';
  },

  // Disconnect GitHub
  [disconnectGitHub.pending.type]: { status: 'loading' as const },
  [disconnectGitHub.fulfilled.type]: (state: GitHubState) => {
    state.currentInstallation = null;
    state.installations = [];
    state.repositories = [];
    state.status = 'success';
    state.error = '';
  },
  [disconnectGitHub.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to disconnect GitHub';
  },

  // Initiate GitHub Connect
  [initiateGitHubConnect.pending.type]: { status: 'loading' as const },
  [initiateGitHubConnect.fulfilled.type]: (state: GitHubState) => {
    state.status = 'success';
    state.error = '';
  },
  [initiateGitHubConnect.rejected.type]: (state: GitHubState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to initiate GitHub connection';
  },
};

// ==============================|| SLICE ||============================== //

export const githubSlice = createSlice({
  name: 'github',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentInstallation(state, action) {
      state.currentInstallation = action.payload;
    },
    clearGitHubState(state) {
      state.currentInstallation = null;
      state.installations = [];
      state.repositories = [];
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentInstallation, clearGitHubState } = githubSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectGitHubInstallations = (state: any) => state.github.installations;
export const selectCurrentInstallation = (state: any) => state.github.currentInstallation;
export const selectGitHubRepositories = (state: any) => state.github.repositories;
export const selectGitHubStatus = (state: any) => state.github.status;
export const selectGitHubError = (state: any) => state.github.error;

export default githubSlice.reducer;
